/**
 * Centered sign-in card, matching Lane's so the two products read as one suite.
 *
 * The mark is `/icon.svg` rather than a letter tile: it is the same asset the
 * favicon and any future PWA install use, so the brand cannot drift between the
 * tab, the dock and this screen. It already carries its own rounded-square
 * background at the same radius the tile used, which is why there is no wrapper.
 *
 * "Pulse" here, rather than "Helpdesk" or "CRM", because signing in is the one
 * moment that belongs to the platform rather than to either product inside it:
 * one session covers both, and the rail is what you land on. The NetXL part is
 * left off -- the rail already carries it, and repeating it in the name read as
 * a company sign-in rather than a product one.
 *
 * There is one button and it names no provider. Sign-in goes to identify, which
 * is where the choice between a password, Google and anything added later
 * actually lives -- so offering "Sign in with Google" here would be this screen
 * asserting something it no longer decides, and would go stale the day a second
 * method is turned on. It also spares anyone the dead end of picking a method
 * their account does not have.
 */
export function Login() {
  const params = new URLSearchParams(window.location.search);
  const error = params.get("error");

  return (
    <div className="flex h-full items-center justify-center bg-app p-4">
      <div className="w-full max-w-sm rounded-2xl bg-surface p-8 text-center shadow-sm ring-1 ring-line">
        <img
          src="/icon.svg"
          alt=""
          width={48}
          height={48}
          /*
           * Decorative: the heading right below already names the product, so a
           * described image would have a screen reader say "Pulse" twice.
           */
          aria-hidden
          className="mx-auto mb-4 h-12 w-12"
        />
        <h1 className="text-xl font-semibold text-content">Sign in to Pulse</h1>
        <p className="mt-1 text-sm text-muted">
          The NetXL support inbox, as tickets.
        </p>

        {error ? (
          <p className="mt-4 rounded-md bg-red-50 p-3 text-left text-sm text-red-700 dark:bg-red-950 dark:text-red-300">
            {error}
          </p>
        ) : null}

        {/*
          A full navigation, not fetch(): the handoff is a redirect chain
          through id.netxl.com, which cannot happen inside XHR.
        */}
        <a
          href="/api/auth/login"
          className="mt-6 inline-flex w-full items-center justify-center rounded-md bg-brand-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-brand-700"
        >
          Sign in
        </a>

        {/*
          Dev-login shortcut, shown only when Vite is serving in dev mode.
          import.meta.env.DEV is replaced with a literal `false` at build time,
          so this whole block is dead code the bundler drops -- it cannot reach
          a production bundle even if the API were somehow misconfigured.
          The API gates the endpoint independently; this is only the button.
        */}
        {import.meta.env.DEV ? (
          <a
            href="/api/auth/dev-login"
            className="mt-2 inline-flex w-full items-center justify-center rounded-md border border-dashed border-line px-4 py-2 text-xs text-muted transition-colors hover:bg-surface-2"
          >
            Dev sign-in (skip identify)
          </a>
        ) : null}
      </div>
    </div>
  );
}
